import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { isDrReddyFromStorage, readStoredUserData } from "../../functions/drReddyOrg";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import LeaderboardCard from "./LeaderboardCard";
import "./leaderboard.css";

function parseBool(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  const s = String(value).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

function resolveBackdrop(themeData, preferMobileViewport) {
  if (!themeData) return "";
  const desk = themeData.background_desk ?? themeData.desktop_view_image;
  const mob = themeData.background_mob ?? themeData.mobile_view_image;
  const ds = desk != null ? String(desk).trim() : "";
  const ms = mob != null ? String(mob).trim() : "";
  if (preferMobileViewport && ms) return ms;
  if (!preferMobileViewport && ds) return ds;
  return ds || ms;
}

export default function Leaderboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);

  /**
   * Dr Reddy's org uses an external leaderboard hosted outside this app.
   * organizationId/sessionId ride along on the redirect so that page can
   * call our `/fetchSessionReport` API back and pull this session's rows.
   */
  useEffect(() => {
    const storedUserData = readStoredUserData();
    const organizationId = storedUserData?.organizationId || "";
    if (isDrReddyFromStorage()) {
      const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
      const sessionId = storedUserData?.sessionId || "";
      const name = storedUserData?.name || "";
      const email = storedUserData?.email || "";
      // Query param names must match the external table's lowercase
      // `userid`/`sessionid` columns exactly — PHP's $_GET is case-sensitive,
      // so sending camelCase `userId`/`sessionId` here silently drops them.
      const params = new URLSearchParams({
        userId: userId,
        sessionid: sessionId,
        name,
        email,
        organizationId,
        game: "game3",
      });
      window.location.href = `https://games.extramileplay.com/custom_thankyou/leaderboard.php?${params.toString()}`;
    }
  }, []);
  /** Keep user on leaderboard — browser Back would otherwise return to the game */
  const lbPathRef = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : "/leaderboard"
  );

  const [narrowViewport, setNarrowViewport] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setNarrowViewport(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    sync();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    const path = lbPathRef.current || "/leaderboard";
    window.history.pushState({ leaderboardExitLock: true }, "", path);
    const onPopState = () => {
      window.history.pushState({ leaderboardExitLock: true }, "", path);
      navigate(path, { replace: true });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate]);

  /** coment */
  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundPosition: body.style.backgroundPosition,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundAttachment: body.style.backgroundAttachment,
      background: body.style.background,
    };
    const hadCommon = body.classList.contains("common-bg");
    body.classList.remove("common-bg");
    body.style.backgroundImage = "none";
    body.style.removeProperty("background-size");
    body.style.removeProperty("background-position");
    body.style.removeProperty("background-repeat");
    body.style.removeProperty("background-attachment");
    body.style.removeProperty("background");
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundAttachment = prev.backgroundAttachment;
      if (prev.background) body.style.background = prev.background;
      else body.style.removeProperty("background");
      if (hadCommon) body.classList.add("common-bg");
    };
  }, []);

  const thankyouEnabled = parseBool(themeData?.thankyou_toggle, false);
  const thankYouText = String(themeData?.custom_text_thank_you_page ?? "").trim();

  const bgRaw = resolveBackdrop(themeData, narrowViewport);
  const bgHref = bgRaw ? getDvSourceImageUrl(bgRaw) : "";

  return (
    <div
      className="lb-stage"
      style={bgHref ? { backgroundImage: toCssUrlValue(bgHref) } : undefined}
    >
      <div className="lb-overlay">
        <LeaderboardCard thankYouText={thankyouEnabled ? thankYouText : ""} />
      </div>
    </div>
  );
}
